import React, { useCallback, useMemo, forwardRef, useState } from "react"
import { MultipleChoiceDropdown } from "dash-styled"
import {
    FetchState,
    getValue,
    hasValue
} from "../../../../../../Models/Client/FetchState"

interface TagsInputDropdownProps {
    tagsFetch: FetchState<string[]>
    value: string[]
    className?: string
    allowAddNewTags: boolean
    rightAlign?: boolean
    noPlaceholder?: boolean
    testId?: string
    onAdd: (value: string, isNew: boolean) => void
    onRemove: (value: string) => void
    onSearch: (keyword: string) => void
}

export const TagsInputDropdown = forwardRef<
    Pick<HTMLInputElement, "focus" | "contains">,
    TagsInputDropdownProps
>(
    (
        {
            tagsFetch,
            value,
            className,
            allowAddNewTags,
            rightAlign = false,
            noPlaceholder = false,
            testId,
            onAdd,
            onRemove,
            onSearch
        },
        ref
    ) => {
        const registeredTags = useMemo(
            () => getValue(tagsFetch) ?? [],
            [tagsFetch]
        )
        const chosenTags = useMemo(
            () => value.filter(tag => registeredTags.includes(tag)),
            [value, registeredTags]
        )
        const newTags = useMemo(
            () => value.filter(tag => !chosenTags.includes(tag)),
            [chosenTags, value]
        )
        const mergedTags = useMemo(
            () => registeredTags.concat(newTags),
            [registeredTags, newTags]
        )
        const [selectedTags, setSelectedTags] = useState<string[]>(chosenTags)
        const handleAddTag = useMemo(
            () =>
                allowAddNewTags
                    ? (tag: string) => {
                          setSelectedTags([...selectedTags, tag])
                          return onAdd(tag, true)
                      }
                    : undefined,
            [allowAddNewTags, selectedTags, onAdd]
        )
        const handleToggleTag = useCallback(
            (affectedTag: string) => {
                setSelectedTags(
                    selectedTags.includes(affectedTag)
                        ? selectedTags.filter(tag => tag !== affectedTag)
                        : [...selectedTags, affectedTag]
                )

                if (value.includes(affectedTag)) {
                    return onRemove(affectedTag)
                }

                onAdd(affectedTag, false)
            },
            [selectedTags, onAdd, onRemove, value]
        )

        return (
            <MultipleChoiceDropdown
                ref={ref}
                size="sm"
                rightAlign={rightAlign}
                className={className}
                values={selectedTags}
                choices={mergedTags}
                placeholder={noPlaceholder ? "" : "Enter tags"}
                addNewText="(new tag)"
                testId={testId}
                isLoadingChoices={!hasValue(tagsFetch)}
                onAdd={handleAddTag}
                onClick={handleToggleTag}
                onSearch={onSearch}
            />
        )
    }
)
